/**
 * "Your order": the lines (quantity, line total, remove), the total, what is
 * left of the voucher, the delivery campus and the save / cancel actions.
 * The same panel sits beside the catalogue on a wide screen and in the
 * bottom sheet on a phone. ReadOnlyOrder is the closed-ordering view.
 */
import { useEffect, useId, useRef, useState } from 'react'
import { CheckCircle2, Loader2, Trash2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import {
  MAX_QUANTITY,
  campusLabel,
  formatRand,
  formatSast,
  lineTotal,
  orderTotal,
  remainingVoucher,
  voucherUsedPercent,
  type DraftLine,
} from '@/lib/staffOrder'
import type {
  StaffOrder,
  StaffOrderCampus,
  StaffOrderItem,
} from '@/services/api/staffOrderService'

import { ItemPicture } from './ItemPicture'
import { QuantityStepper } from './QuantityStepper'
import { selectClass } from './styles'

export const CAMPUS_PROMPT =
  'Choose the campus closest to where you work — your order will be delivered there.'
export const FALLS_AWAY = "Any part of your voucher you don't use falls away."

type LineKey = Pick<DraftLine, 'item_id' | 'size'>

const UNAVAILABLE = 'An item that is no longer available'

// ---------------------------------------------------------------------------
// Totals + voucher bar (shared by both views)
// ---------------------------------------------------------------------------

function VoucherSummary({ totalCents, voucherCents }: { totalCents: number; voucherCents: number }) {
  const left = remainingVoucher(voucherCents, totalCents)
  const over = left < 0
  const used = voucherUsedPercent(voucherCents, totalCents)
  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between text-sm">
        <span className="font-medium text-gray-700">Total</span>
        <span className="text-base font-semibold tabular-nums text-gray-900">{formatRand(totalCents)}</span>
      </div>
      <div
        className="h-2 w-full overflow-hidden rounded-full bg-gray-100"
        role="progressbar"
        aria-label="Voucher used"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(used)}
      >
        <div
          className={cn('h-full rounded-full transition-all', over ? 'bg-red-500' : 'bg-future-blue')}
          style={{ width: `${used}%` }}
        />
      </div>
      <div className="flex items-baseline justify-between text-sm">
        <span className="text-gray-600">{over ? 'Over your voucher by' : 'Left on your voucher'}</span>
        <span className={cn('font-medium tabular-nums', over ? 'text-red-600' : 'text-gray-900')}>
          {formatRand(Math.abs(left))}
        </span>
      </div>
      <p className="text-xs text-gray-500">{FALLS_AWAY}</p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// The editable panel
// ---------------------------------------------------------------------------

export interface OrderPanelProps {
  /** The lines as shown: as placed while nothing has changed, else at today's prices. */
  lines: DraftLine[]
  /**
   * The same lines at today's prices — what a save is charged at — for the
   * voucher checks. Same order as `lines`; defaults to `lines`.
   */
  checkLines?: DraftLine[]
  /** A change re-prices lines placed at an older price: say so. */
  pricesChanged?: boolean
  itemsById: Map<string, StaffOrderItem>
  voucherCents: number
  campuses: StaffOrderCampus[]
  campus: string
  onCampusChange: (code: string) => void
  campusMissing: boolean
  hasOrder: boolean
  dirty: boolean
  saving: boolean
  cancelling: boolean
  /** Ordering closes at — for the cancel confirmation. */
  closesAt: string | null
  /** When the saved order last changed. */
  lastChanged: string | null
  notice: string | null
  error: string | null
  onQuantity: (key: LineKey, quantity: number) => void
  onRemove: (key: LineKey) => void
  onSave: () => void
  onCancelOrder: () => void
  onUndo: () => void
  /** The sheet on a phone has its own title. */
  hideHeading?: boolean
}

export function OrderPanel(props: OrderPanelProps) {
  const {
    lines, checkLines = lines, pricesChanged = false, itemsById, voucherCents, campuses, campus,
    onCampusChange, campusMissing, hasOrder, dirty, saving, cancelling, closesAt, lastChanged, notice,
    error, onQuantity, onRemove, onSave, onCancelOrder, onUndo, hideHeading,
  } = props
  const [confirmingCancel, setConfirmingCancel] = useState(false)
  const campusRef = useRef<HTMLSelectElement | null>(null)
  const campusId = useId()
  const campusHelpId = useId()
  const campusErrorId = useId()

  // "Place order" without a campus: take the person to the field that needs them.
  useEffect(() => {
    if (campusMissing) campusRef.current?.focus()
  }, [campusMissing])

  const total = orderTotal(lines)
  // What a save would be charged: the placed order stands as it is until something changes.
  const totalNow = orderTotal(checkLines)
  const over = (hasOrder && !dirty ? total : totalNow) > voucherCents
  const busy = saving || cancelling
  const hasUnavailable = lines.some((l) => !itemsById.has(l.item_id))
  const saveDisabled = busy || lines.length === 0 || over || hasUnavailable || (hasOrder && !dirty)

  let saveHint: string | null = null
  if (lines.length === 0) {
    saveHint = hasOrder
      ? 'Your order needs at least one item. To cancel it instead, use "Cancel my order".'
      : 'Add at least one item to place your order.'
  } else if (over) {
    saveHint = `Your order comes to ${formatRand(totalNow)}, which is more than your voucher of ${formatRand(voucherCents)}.`
  }

  return (
    <section aria-label="Your order" className="space-y-4">
      {!hideHeading && (
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-lg font-semibold text-heritage-blue-700">Your order</h2>
          {hasOrder && !dirty && (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
              <CheckCircle2 className="h-3.5 w-3.5" />
              Placed
            </span>
          )}
        </div>
      )}

      {lines.length === 0 ? (
        <p className="rounded-md border border-dashed border-gray-200 px-3 py-6 text-center text-sm text-gray-500">
          Nothing in your order yet. Choose a size and add items from the list.
        </p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {lines.map((line, i) => {
            const item = itemsById.get(line.item_id)
            const name = item?.name ?? UNAVAILABLE
            const priceNow = checkLines[i]?.unit_price_cents ?? line.unit_price_cents
            const canIncrease = line.quantity < MAX_QUANTITY && totalNow + priceNow <= voucherCents
            return (
              <li key={`${line.item_id}|${line.size}`} className="flex gap-3 py-3">
                <ItemPicture src={item?.image_url} alt={name} className="h-14 w-14 shrink-0 rounded-md border border-gray-100" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium leading-snug text-heritage-blue-700">{name}</p>
                  <p className="text-xs text-gray-500">
                    Size {line.size} · {formatRand(line.unit_price_cents)} each
                  </p>
                  {(!item || item.available === false) && (
                    <p className="text-xs text-red-600">
                      {item ? 'No longer available. ' : ''}Please remove it before you save your order.
                    </p>
                  )}
                  <div className="mt-2 flex items-center justify-between gap-2">
                    <QuantityStepper
                      compact
                      value={line.quantity}
                      onChange={(q) => onQuantity(line, q)}
                      canIncrease={canIncrease}
                      disabled={busy}
                      label={`Quantity of ${name}, size ${line.size}`}
                    />
                    <span className="text-sm font-semibold tabular-nums text-gray-900">
                      {formatRand(lineTotal(line))}
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => onRemove(line)}
                  disabled={busy}
                  className="-mr-1 h-8 w-8 shrink-0 rounded-md p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
                  aria-label={`Remove ${name}, size ${line.size}`}
                  title="Remove"
                >
                  <Trash2 className="h-5 w-5" />
                </button>
              </li>
            )
          })}
        </ul>
      )}

      {pricesChanged && (
        <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
          Some prices have changed since you placed your order. When you save, the whole order is
          charged at today&apos;s prices.
        </p>
      )}

      <VoucherSummary totalCents={total} voucherCents={voucherCents} />

      <div className="space-y-1">
        <label htmlFor={campusId} className="text-sm font-medium text-gray-700">
          Delivery campus
        </label>
        <p id={campusHelpId} className="text-xs text-gray-500">
          {CAMPUS_PROMPT}
        </p>
        <select
          id={campusId}
          ref={campusRef}
          className={selectClass}
          value={campus}
          required
          disabled={busy}
          aria-describedby={campusMissing ? `${campusHelpId} ${campusErrorId}` : campusHelpId}
          aria-invalid={campusMissing ? true : undefined}
          onChange={(e) => onCampusChange(e.target.value)}
        >
          <option value="" disabled>
            Choose a campus
          </option>
          {campuses.map((c) => (
            <option key={c.code} value={c.code}>
              {c.label}
            </option>
          ))}
        </select>
        {campusMissing && (
          <p id={campusErrorId} className="text-xs text-red-600">
            Please choose a delivery campus.
          </p>
        )}
      </div>

      {notice && (
        <div className="flex gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800" role="status">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{notice}</span>
        </div>
      )}
      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
          {error}
        </div>
      )}

      <div className="space-y-2">
        <Button type="button" className="h-11 w-full text-base" onClick={onSave} disabled={saveDisabled}>
          {saving && <Loader2 className="h-4 w-4 animate-spin" />}
          {hasOrder ? 'Update order' : 'Place order'}
        </Button>
        {saveHint && <p className="text-xs text-gray-500">{saveHint}</p>}
        {hasOrder && dirty && !busy && (
          <p className="text-xs text-gray-500">
            You have changes that are not saved yet.{' '}
            <button type="button" className="font-medium text-future-blue-700 hover:underline" onClick={onUndo}>
              Undo my changes
            </button>
          </p>
        )}
        {hasOrder && !dirty && lastChanged && (
          <p className="text-xs text-gray-500">Last changed {formatSast(lastChanged)}.</p>
        )}
      </div>

      {hasOrder && (
        <div className="border-t border-gray-100 pt-3">
          {confirmingCancel ? (
            <div className="space-y-2 rounded-md border border-red-200 bg-red-50 p-3" role="group" aria-label="Cancel your order?">
              <p className="text-sm font-medium text-red-800">Cancel your order?</p>
              <p className="text-xs text-red-700">
                Nothing will be ordered for you.
                {closesAt ? ` You can still place a new order until ${formatSast(closesAt)}.` : ''}
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  disabled={busy}
                  onClick={() => {
                    setConfirmingCancel(false)
                    onCancelOrder()
                  }}
                >
                  Yes, cancel my order
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => setConfirmingCancel(false)}>
                  Keep my order
                </Button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className="inline-flex items-center gap-1 text-sm font-medium text-red-600 hover:underline disabled:opacity-50"
              onClick={() => setConfirmingCancel(true)}
              disabled={busy}
            >
              {cancelling && <Loader2 className="h-4 w-4 animate-spin" />}
              Cancel my order
            </button>
          )}
        </div>
      )}
    </section>
  )
}

// ---------------------------------------------------------------------------
// Read-only view (ordering closed)
// ---------------------------------------------------------------------------

export function ReadOnlyOrder({
  order,
  itemsById,
  voucherCents,
  campuses,
  roundClosed,
}: {
  order: StaffOrder | null
  itemsById: Map<string, StaffOrderItem>
  voucherCents: number
  campuses: StaffOrderCampus[]
  roundClosed: boolean
}) {
  if (!order || order.lines.length === 0) {
    return (
      <p className="py-4 text-center text-sm text-gray-500">
        {roundClosed ? 'You did not place an order.' : 'You have not placed an order.'}
      </p>
    )
  }
  return (
    <div className="space-y-4">
      <ul className="divide-y divide-gray-100">
        {order.lines.map((line) => {
          const item = itemsById.get(line.item_id)
          const name = item?.name ?? 'Item'
          return (
            <li key={`${line.item_id}|${line.size}`} className="flex items-center gap-3 py-3">
              <ItemPicture src={item?.image_url} alt={name} className="h-14 w-14 shrink-0 rounded-md border border-gray-100" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium leading-snug text-heritage-blue-700">{name}</p>
                <p className="text-xs text-gray-500">
                  Size {line.size} · {line.quantity} × {formatRand(line.unit_price_cents)}
                </p>
              </div>
              <span className="text-sm font-semibold tabular-nums text-gray-900">
                {formatRand(line.quantity * line.unit_price_cents)}
              </span>
            </li>
          )
        })}
      </ul>
      <VoucherSummary totalCents={order.total_cents} voucherCents={voucherCents} />
      <dl className="space-y-1 text-sm">
        <div className="flex justify-between gap-3">
          <dt className="text-gray-600">Delivery campus</dt>
          <dd className="text-right font-medium text-gray-900">{campusLabel(campuses, order.delivery_campus)}</dd>
        </div>
        {order.updated_at && (
          <div className="flex justify-between gap-3">
            <dt className="text-gray-600">Last changed</dt>
            <dd className="text-right text-gray-900">{formatSast(order.updated_at)}</dd>
          </div>
        )}
      </dl>
    </div>
  )
}
